import React, { useState } from 'react';
import axios from 'axios';

interface Props {
  email: string;
}

const DISMISS_KEY = 'email_verify_banner_dismissed';
const RESEND_COOLDOWN_MS = 60_000;

const EmailVerificationBanner: React.FC<Props> = ({ email }) => {
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === '1'
  );
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [cooldownUntil, setCooldownUntil] = useState(0);

  if (dismissed) return null;

  const secondsLeft = Math.max(0, Math.ceil((cooldownUntil - Date.now()) / 1000));
  const canResend = status !== 'sending' && secondsLeft === 0;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleResend = async () => {
    if (!canResend) return;
    setStatus('sending');
    try {
      await axios.post('/api/auth/resend-verification');
      setStatus('sent');
      setCooldownUntil(Date.now() + RESEND_COOLDOWN_MS);
      setTimeout(() => setStatus('idle'), RESEND_COOLDOWN_MS);
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 4000);
    }
  };

  return (
    <div className="fixed top-[60px] inset-x-0 z-40 flex justify-center px-4 pt-2 pointer-events-none">
      <div className="pointer-events-auto w-full max-w-2xl rounded-2xl border border-amber-500/30 bg-amber-500/10 backdrop-blur-md px-4 py-3 flex items-center gap-3 shadow-lg">
        {/* Icon */}
        <div className="shrink-0 w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
          <svg className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>

        {/* Text */}
        <p className="flex-1 text-sm text-amber-200/90">
          Please verify your email{' '}
          <span className="font-semibold text-amber-100">{email}</span>
          {' '}to receive pattern alerts.
        </p>

        {/* Resend button */}
        <button
          onClick={handleResend}
          disabled={!canResend}
          className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed
            bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
        >
          {status === 'sending' && 'Sending…'}
          {status === 'sent' && secondsLeft > 0 && `Sent · ${secondsLeft}s`}
          {status === 'error' && 'Failed — retry'}
          {status === 'idle' && 'Resend email'}
        </button>

        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-amber-400/60 hover:text-amber-300 hover:bg-amber-500/10 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
